import Image from 'next/future/image';
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { usePopper } from 'react-popper';
import cn from 'classnames';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { extractTags } from '@lambda/reebelo/src/tagHelpers';
import { RC_DURATION } from '@lambda/reebelo';
import { WarrantyT } from '@lambda/apis/src/warranty/types';
import { CtaButton, CtaLink } from '@/components/Cta/Cta';
import blackTick from '@/public/icons/account/blackTick.svg';
import placeholderProductImage from '@/public/images/smartForm/no-image-placeholder.svg';
import close from '@/public/icons/account/close.svg';
import {
  isWithinFreeReturnPeriod,
  isWithinWarrantyCoverage,
  getDeliveryDateFromShipment,
} from '@/lib/smartForm/orderUnitUtil';
import {
  ShipmentStatus,
  ShipmentLineItem,
  Shipment,
  CustomerOrder,
  LineItemsT,
} from '@/lib/smartForm/types';
import {
  getDaysSinceDate,
  handleExtractFulfillmentStatus,
} from '@/lib/smartForm/helper';
import FulfillmentStatusBar from './FulfillmentStatusBar';
import TrackingDetails from './TrackingDetail';
import settings from '@/settings/configs';
import { selectWarranty } from '@/lib/collections/catalogue/helpers';
import AddReebeloCareCta from './AddReebeloCareCta';
import useCart from '@/lib/use-cart';
import BuyAgain from './BuyAgain';
import { generateBatteryHealthTitle } from '@/lib/collections/helpers';

const smartformButtonCommonCSS =
  'w-2/5 flex-1 border bg-white pt-2 pb-2 text-center text-sm font-bold text-gray-700 duration-200 ease-in-out sm:w-full sm:flex-none sm:font-normal lg:w-full lg:border lg:px-4 lg:pt-2 lg:pb-2 lg:!text-base';
const RETURN = 'return';
const WARRANTY_CLAIM = 'warrantyClaim';
const SELLER = 'Seller';
const REEBELOCARE_EXTENDED = 'ReebeloCare Extended';

const SmartformButton = ({
  buttonType,
  isCovered,
  orderNumber,
  lineItemId,
  shouldShowButton,
  rcOrderId,
}: {
  buttonType: typeof RETURN | typeof WARRANTY_CLAIM;
  isCovered: boolean;
  orderNumber: string;
  lineItemId: string;
  shouldShowButton: boolean;
  lineItem?: ShipmentLineItem;
  rcOrderId?: string;
}) => {
  const router = useRouter();
  const { store } = settings;

  const [referenceElement, setReferenceElement] = useState<any>(null);
  const [popperElement, setPopperElement] = useState<any>(null);
  const [arrowElement, setArrowElement] = useState<any>(null);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'top-start',
    modifiers: [
      {
        name: 'arrow',
        options: {
          element: arrowElement,
        },
      },
      { name: 'offset', options: { offset: [0, 4] } },
    ],
  });

  const [isHover, setIsHover] = useState(false);

  const isUSStore = store === 'reebelo-us' || store === 'reebelo-dev';
  const warrantyLabel = isUSStore ? 'Warranty' : 'Warranty Claim';

  return (
    <>
      {isHover && !isCovered && (
        <div
          ref={setPopperElement}
          // eslint-disable-next-line
          style={styles.popper}
          {...attributes.popper}
          className="z-40"
        >
          <div
            className={cn(
              'w-72 rounded-lg bg-gray-700 px-1 text-white',
              {
                'h-28': buttonType === RETURN,
              },
              {
                'h-24': buttonType === WARRANTY_CLAIM,
              },
            )}
          >
            <p className="p-4 text-sm">
              {buttonType === RETURN
                ? 'Your item can no longer be returned since it has exceeded the return period. Please submit a Warranty Claim instead.'
                : 'Your device is outside of the Warranty Coverage Period.'}
            </p>
            <div
              className={cn(
                'absolute rotate-45',
                {
                  'right-0 mt-3 mr-8 sm:right-auto sm:ml-8 sm:mr-auto':
                    buttonType === WARRANTY_CLAIM,
                },
                {
                  'ml-8 -mt-3': buttonType === RETURN,
                },
              )}
            >
              <span
                className="h-4 w-4 bg-gray-700"
                // eslint-disable-next-line
                style={styles.arrow}
                ref={setArrowElement}
              />
            </div>
          </div>
        </div>
      )}
      {isCovered && shouldShowButton ? (
        <CtaButton
          variant="white"
          className={cn(
            'border-gray-700 text-gray-700 hover:bg-gray-700 hover:text-white hover:!shadow-none lg:border-gray-700 lg:hover:text-white',
            smartformButtonCommonCSS,
          )}
          onClick={() => {
            router.push(
              `/account/order/${orderNumber}/${buttonType}?lineItemId=${lineItemId}${
                rcOrderId ? `&rcOrderId=${rcOrderId}` : ''
              }`,
              undefined,
              {
                shallow: true,
              },
            );
          }}
        >
          {' '}
          {buttonType === RETURN ? 'Return' : warrantyLabel}
        </CtaButton>
      ) : (
        <CtaButton
          variant="white"
          className={cn(
            'border-gray-700/40 text-gray-700/40 hover:!shadow-none lg:border-gray-700/40 ',
            smartformButtonCommonCSS,
          )}
          onMouseOver={() => setIsHover(true)}
          onMouseOut={() => setIsHover(false)}
          ref={setReferenceElement}
        >
          {buttonType === RETURN ? 'Return' : warrantyLabel}
        </CtaButton>
      )}
    </>
  );
};

const WarrantyInfo = ({
  isRefunded,
  isCoveredByWarranty,
  isReebeloCare,
  isItemDelivered,
  duration,
}: {
  isRefunded: boolean;
  isCoveredByWarranty: boolean;
  isReebeloCare: boolean;
  isItemDelivered: boolean;
  duration: RC_DURATION;
}) => {
  if (isRefunded) return null;

  const isUSStore = ['reebelo-dev', 'reebelo-us'].includes(settings.store);

  const warrantyText = (() => {
    // before item is delivered will always show covered text
    const defaultWarrantyText = `Covered by ${
      isReebeloCare ? REEBELOCARE_EXTENDED : SELLER
    } Warranty`;

    const UsWarrantyText = `Covered by ${
      isReebeloCare ? `ReebeloCare ${duration}-Months' Extended` : SELLER
    } Warranty`;

    const _warrantyText = isUSStore ? UsWarrantyText : defaultWarrantyText;

    if (isItemDelivered) {
      if (isCoveredByWarranty) return _warrantyText;

      return `Out of ${
        isReebeloCare ? REEBELOCARE_EXTENDED : SELLER
      } Warranty's Coverage Period`;
    }

    return _warrantyText;
  })();

  const displayCoveredWarranty =
    !isItemDelivered || (isItemDelivered && isCoveredByWarranty);

  return (
    <div
      className={cn('flex w-full items-center', {
        'h-[11px]': !displayCoveredWarranty,
      })}
    >
      <div
        className={cn(
          'mr-1 flex w-[11px] items-center justify-center',
          {
            'h-[11px]': displayCoveredWarranty,
          },
          {
            'align-middle': !displayCoveredWarranty,
          },
        )}
      >
        {displayCoveredWarranty ? (
          <Image width={11} height={11} alt="tick" src={blackTick} />
        ) : (
          <Image width={9} height={9} alt="close" src={close} />
        )}
      </div>
      <span
        className={cn('w-full text-xs', {
          'mt-1': !displayCoveredWarranty,
        })}
      >
        {warrantyText}
      </span>
    </div>
  );
};

const OrderItem = ({
  deliveryDate,
  storeTimeZone,
  orderNumber,
  lineItem,
  reebeloCares,
  isRefunded,
  shipmentStatus,
  processedAt,
  order,
  lineItems,
}: {
  deliveryDate: string | null;
  storeTimeZone: string;
  orderNumber: string;
  lineItem: ShipmentLineItem;
  reebeloCares: string[];
  isRefunded: boolean;
  shipmentStatus: ShipmentStatus;
  processedAt: string | null;
  order: CustomerOrder;
  lineItems: LineItemsT;
}) => {
  const { cart } = useCart();
  const [productWarranty, setProductWarranty] = useState<
    WarrantyT | undefined
  >();

  const [imgSrc, setImgSrc] = useState(
    lineItem.image ?? placeholderProductImage,
  );

  const isUSStore = ['reebelo-us', 'reebelo-dev'].includes(settings.store);
  const isWarrantyEligible =
    !lineItem.reebeloCare.isCovered && lineItem.reebeloCare.isWarrantyEligible;

  const { category, batteryHealth } = extractTags(lineItem.tags);

  dayjs.extend(isSameOrAfter);

  const isCoveredByCG =
    lineItem.reebeloCare.isCovered &&
    lineItem.reebeloCare.reebeloCareVendor === 'Cover Genius';

  const isReebeloCare = reebeloCares.some(
    (reebeloCare) =>
      reebeloCare.toLowerCase().indexOf(lineItem.name.toLowerCase()) > -1,
  );

  const reebeloCareLineItem = lineItems.find((l) => {
    const rcFor = l.attributes?.find((att) => att.key === 'For')?.value;

    return rcFor?.toLowerCase().includes(lineItem.name.toLowerCase());
  });

  const reebeloCareDuration = (reebeloCareLineItem?.attributes?.find(
    (att) => att.key === 'Duration',
  )?.value ?? '24') as RC_DURATION;

  const isCoveredByWarranty = isWithinWarrantyCoverage(
    deliveryDate,
    isReebeloCare,
    storeTimeZone,
    reebeloCareDuration,
  );

  const isCoveredByFreeReturn = isWithinFreeReturnPeriod(
    deliveryDate,
    storeTimeZone,
    processedAt,
  );

  const isItemDelivered = shipmentStatus === 'delivered';
  const isItemShipped = shipmentStatus === 'ordered';

  const spec = lineItem.name.split(' - ');

  if (batteryHealth)
    spec.push(`${generateBatteryHealthTitle(batteryHealth)} Battery`);
  const description = spec.slice(1).join(' - ');

  const getTitle = () => {
    const title = spec[0];

    return title;
  };

  const title = getTitle();

  // Date when CG Policy Creation was updated '2023-05-31T08:00:00+08:00' | RT-4753
  const isAfterCGUpdates = dayjs(processedAt)
    .utc()
    .isSameOrAfter(dayjs('2023-05-31T08:00:00+08:00').utc());

  const shouldShowWarrantyButton =
    isItemDelivered ||
    (isItemShipped && isReebeloCare && isCoveredByCG && isAfterCGUpdates);

  const daysSinceOrder = getDaysSinceDate(
    processedAt ?? dayjs().format('Ddd MMMM YYYY'),
    storeTimeZone,
  );

  const isPostPurchaseRcInCart = Boolean(
    cart?.lines.edges.some(
      (edge) =>
        edge.node.attributes.some(
          (attr) => attr.value === `[${lineItem.sku}] ${lineItem.name}`,
        ) &&
        edge.node.attributes.some((attr) => attr.value === `${orderNumber}`),
    ),
  );

  const shoudShowAddReebeloCare =
    isUSStore &&
    daysSinceOrder <= 30 &&
    productWarranty?.shopifyVariantId &&
    isWarrantyEligible &&
    !isRefunded;

  useEffect(() => {
    const handleWarrantySelection = async () => {
      if (!isWarrantyEligible || !lineItem) return;

      const warranty = await selectWarranty(
        lineItem.price,
        RC_DURATION['24_MONTH'],
        category,
      );

      setProductWarranty(warranty);
    };

    handleWarrantySelection();
  }, [lineItem]);

  return (
    <div className="mb-3.5 w-full flex-wrap border-b border-gray-200 pb-3.5 last:border-b-0 sm:flex xl:flex-nowrap">
      <div className="mt-6 flex gap-4 sm:mt-0">
        <div className="mt-1 flex h-[100px] w-[100px] min-w-[100px] items-center justify-center xxs:min-w-[100px] xs:min-w-[100px]">
          <Image
            height={80}
            width={80}
            alt="product image"
            className="mt-3 h-full object-contain"
            src={imgSrc}
            onError={() => {
              setImgSrc(placeholderProductImage);
            }}
          />
        </div>
        <div className="flex flex-row flex-wrap text-sm lg:py-4">
          <div className="mb-4">
            <h2 className="w-full font-bold">
              {title} (x{lineItem.quantity})
            </h2>
            <span className="w-full">{description}</span>
            <BuyAgain
              variantId={lineItem?.bestOffer?.variantId}
              productId={lineItem?.bestOffer?.productId}
              vendor={lineItem.vendor}
              category={category as string}
            />
          </div>
          <WarrantyInfo
            isRefunded={isRefunded}
            isReebeloCare={isReebeloCare}
            isCoveredByWarranty={isCoveredByWarranty}
            isItemDelivered={isItemDelivered}
            duration={reebeloCareDuration}
          />
          {shoudShowAddReebeloCare && (
            <AddReebeloCareCta
              productWarranty={productWarranty}
              orderNumber={order.orderNumber}
              warrantyAddedToCart={isPostPurchaseRcInCart}
              lineItem={lineItem}
            />
          )}
        </div>
      </div>

      {!isRefunded && shouldShowWarrantyButton ? (
        <div className="flex w-full min-w-[175px] pt-6 pl-0 sm:block sm:pt-4 xl:w-auto xl:pl-2 xl:pt-0">
          <div className="contents w-full gap-4 space-x-2 sm:block sm:gap-0 sm:space-x-0 xl:w-auto">
            <SmartformButton
              buttonType="return"
              orderNumber={orderNumber}
              isCovered={isCoveredByFreeReturn}
              shouldShowButton={isItemDelivered}
              lineItemId={lineItem.id}
              rcOrderId={
                lineItem.reebeloCare.isCovered
                  ? lineItem.reebeloCare.rcOrderId
                  : undefined
              }
            />
            <SmartformButton
              buttonType="warrantyClaim"
              orderNumber={orderNumber}
              isCovered={isCoveredByWarranty}
              shouldShowButton={shouldShowWarrantyButton}
              lineItemId={lineItem.id}
              lineItem={lineItem}
              rcOrderId={
                lineItem.reebeloCare.isCovered
                  ? lineItem.reebeloCare.rcOrderId
                  : undefined
              }
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

const DownloadInvoices = ({ invoiceUrl }: { invoiceUrl?: string }) => (
  <div>
    {invoiceUrl ? (
      <>
        <div className="hidden sm:block">
          <a className="font-bold" href={invoiceUrl} target="_blank">
            Download Invoice
          </a>
        </div>
        <div className="block sm:hidden">
          <CtaLink
            variant="outlined"
            target="_blank"
            href={invoiceUrl}
            download
            className="block w-full p-3 font-bold"
          >
            {`Download Invoice`}
          </CtaLink>
        </div>
      </>
    ) : (
      <p className="text-gray-700/40">{`Invoice Not Available`}</p>
    )}
  </div>
);

export default function ShipmentDetail({
  shipment,
  shipmentIndex,
  processedAt,
  reebeloCares,
  order,
  storeTimeZone,
  lineItems,
}: {
  shipment: Shipment;
  shipmentIndex: number;
  processedAt: string;
  reebeloCares: string[];
  order: CustomerOrder;
  storeTimeZone: string;
  lineItems: LineItemsT;
}) {
  const { canceledAt, orderNumber, fulfillmentStatus } = order;

  const isRefunded = shipment.lineItems.some((lineItem) => lineItem.isRefunded);

  const vendors = Array.from(
    new Set(shipment.lineItems.flatMap((lineItem) => lineItem.vendor)),
  );

  // find the latest delivery date
  const shipmentDeliveryDate = getDeliveryDateFromShipment(shipment);

  const status: ShipmentStatus = handleExtractFulfillmentStatus(
    shipment,
    shipmentDeliveryDate,
    fulfillmentStatus,
  );

  /*
   * This following will handle edge cases where no tracking
   * is available for fulfilled orders from long time before
   */

  const deliveryDate =
    shipmentDeliveryDate === null && fulfillmentStatus === 'FULFILLED'
      ? processedAt
      : shipmentDeliveryDate;

  const shipmentLineItemsAreCancelled = shipment.lineItems.every(
    (lineItem) => lineItem.isRefunded,
  );

  return (
    <>
      <div className="mt-3 w-full rounded border border-gray-200 bg-white sm:flex">
        <div className="w-full border-0 border-gray-200 px-4 py-3.5 sm:w-1/2 sm:border-r 2xl:w-1/2">
          {!shipmentLineItemsAreCancelled && (
            <div>
              <div className="flex sm:flex-none">
                <h3 className="mb-3.5 mt-2 flex-1 text-sm font-bold leading-tight sm:mt-0 sm:flex-none">
                  {status !== 'delivered'
                    ? 'Awaiting Shipment'
                    : `Shipment ${shipmentIndex}`}
                </h3>

                <a
                  className="mb-3.5 mr-5 mt-2 block h-3 w-3 text-right text-sm underline underline-offset-4 sm:hidden"
                  href={`/account/order/${orderNumber}/help?vendorName=${encodeURIComponent(
                    vendors[0],
                  )}`}
                >
                  Help
                </a>
              </div>
            </div>
          )}
          <div className="static mt-8 sm:mt-0 sm:hidden">
            <div className="-mt-8 w-full border border-solid border-gray-300"></div>
          </div>
          {shipment.lineItems.map((lineItem, idx) => (
            <>
              <OrderItem
                key={idx}
                lineItem={lineItem}
                storeTimeZone={storeTimeZone}
                deliveryDate={deliveryDate}
                reebeloCares={reebeloCares}
                orderNumber={orderNumber}
                isRefunded={lineItem.isRefunded}
                shipmentStatus={status}
                processedAt={processedAt}
                order={order}
                lineItems={lineItems}
              />
              <div className="justify-between font-bold sm:flex">
                <span className="my-auto">
                  <DownloadInvoices invoiceUrl={shipment.invoiceUrl} />
                </span>
                <a
                  className="hidden sm:block sm:py-2"
                  href={`/account/order/${orderNumber}/help?vendorName=${encodeURIComponent(
                    lineItem.vendor,
                  )}`}
                >
                  Help
                </a>
              </div>
            </>
          ))}
        </div>
        <div className="flex w-full flex-wrap items-start border-r border-gray-200 sm:w-1/2 2xl:w-1/2">
          <FulfillmentStatusBar
            status={status}
            isRefunded={isRefunded}
            canceledAt={canceledAt}
            orderedDate={processedAt}
            storeTimeZone={storeTimeZone}
            deliveredDate={shipment.deliveredBy}
            dispatchedDate={shipment.dispatchedAt}
          />
          {canceledAt == null && !isRefunded && (
            <TrackingDetails
              shipment={shipment}
              shipmentStatus={status}
              storeTimeZone={storeTimeZone}
            />
          )}
        </div>
      </div>
    </>
  );
}
